/**
 * Parse production schedule CSV exports (client-side).
 * Counts unique shoot days and locations for the onboard confirm step.
 */

const SHOOT_DAY_ALIASES = [
  'shoot day',
  'shootday',
  'day',
  'day #',
  'day number',
  'shooting day',
  'sd',
];
const LOCATION_ALIASES = [
  'location',
  'locations',
  'set',
  'set location',
  'shoot location',
  'place',
];
const DATE_ALIASES = ['date', 'shoot date', 'shooting date', 'day date'];
const SCENE_ALIASES = ['scene', 'scene #', 'scene number', 'sc'];
const UNIT_ALIASES = ['unit', 'unit name', 'camera unit'];
const NOTES_ALIASES = ['notes', 'note', 'comments'];

function normalizeHeader(h) {
  return String(h || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function splitCsvLine(line) {
  const fields = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      fields.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields.map((f) => f.trim());
}

function findColIndex(headers, aliases) {
  const normalized = headers.map(normalizeHeader);
  return normalized.findIndex((h) => aliases.includes(h));
}

function fieldAt(fields, idx) {
  if (idx < 0 || idx >= fields.length) return '';
  return (fields[idx] || '').trim();
}

/**
 * @param {string} text Raw CSV file contents
 * @returns {{
 *   shootDays: number,
 *   locationCount: number,
 *   rowCount: number,
 *   rows: Array<{ scene: string, shootDay: string, date: string, unit: string, location: string, notes: string }>,
 *   warnings: string[],
 * }}
 */
export function parseScheduleCsv(text) {
  const warnings = [];
  const raw = String(text || '').replace(/^\uFEFF/, '').trim();
  if (!raw) {
    throw new Error('The schedule file is empty.');
  }

  const lines = raw.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) {
    throw new Error('The schedule CSV must include a header row and at least one row.');
  }

  const headers = splitCsvLine(lines[0]);
  const shootDayIdx = findColIndex(headers, SHOOT_DAY_ALIASES);
  const locationIdx = findColIndex(headers, LOCATION_ALIASES);
  const dateIdx = findColIndex(headers, DATE_ALIASES);
  const sceneIdx = findColIndex(headers, SCENE_ALIASES);
  const unitIdx = findColIndex(headers, UNIT_ALIASES);
  const notesIdx = findColIndex(headers, NOTES_ALIASES);

  if (shootDayIdx < 0 && dateIdx < 0) {
    throw new Error('Could not find a Shoot Day or Date column in this schedule CSV.');
  }
  if (locationIdx < 0) {
    warnings.push('No Location column found - location count may be empty.');
  }

  const rows = [];
  const shootDayKeys = new Set();
  const locations = new Set();

  for (let i = 1; i < lines.length; i += 1) {
    const fields = splitCsvLine(lines[i]);
    if (!fields.some((f) => f)) continue;

    const shootDay = fieldAt(fields, shootDayIdx);
    const date = fieldAt(fields, dateIdx);
    const location = fieldAt(fields, locationIdx);
    const scene = fieldAt(fields, sceneIdx);
    const unit = fieldAt(fields, unitIdx);
    const notes = fieldAt(fields, notesIdx);

    const dayKey = shootDay || date;
    if (dayKey) shootDayKeys.add(dayKey);
    if (location) locations.add(location);

    rows.push({
      scene,
      shootDay: shootDay || dayKey,
      date,
      unit,
      location,
      notes,
    });
  }

  if (!rows.length) {
    throw new Error('No schedule rows found in this CSV.');
  }

  return {
    shootDays: shootDayKeys.size,
    locationCount: locations.size,
    rowCount: rows.length,
    rows,
    warnings,
  };
}
